import React, { useMemo, useState } from "react";
import { useCreateInternalStateBranch } from "../../state/InternalContext";

interface InjectElementRulesProps {
  children: React.ReactNode,
}

/***
 * A component that adds elements to another element based on rules.
 * The rules are defined in child elements of the InjectElementRules element,
 * and can use data in the state to determine whether the element should be added.
 * The rules are evaluated by the renderNode helper function.
 * This method is primarily intended for adding markup to SVG images.
 */
export function InjectElementRules({ children }: InjectElementRulesProps) {
  const createInternalStateBranch = useCreateInternalStateBranch();
  type InjectElementRule = {
    id: string,                     // Apply to element with this id
    element: React.ReactElement,    // Element to inject as parent of element with matching id
  }

  const parseRule = (ruleElement: React.ReactElement): InjectElementRule | null => {
    const id = ruleElement.props.applyToId;
    const children = React.Children.toArray(ruleElement.props.children);
    const firstChildElement = children[0] as React.ReactElement;
    return (id && firstChildElement && { id: id, element: firstChildElement } as InjectElementRule) || null;
  };

  const [rules, setRules] = useState<Array<InjectElementRule>>([]);
  const [filteredChildren, setFilteredChildren] = useState<Array<React.ReactNode>>([]);

  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && (child.type.toString().toLowerCase() === "rule")) {
      const rule = parseRule(child);
      rule && setRules(prev => [...prev, rule]);
    }
    else {
      setFilteredChildren(prev => [...prev, child]);
    }
  });

  // Create a new state branch used in child components in the InjectElementRules element
  // and add the injectElementRules to the new state branch
  const BranchComponent = useMemo(() => createInternalStateBranch({
    injectElementRules: rules,
  }), [rules, createInternalStateBranch]);

  return <BranchComponent>
    {filteredChildren}
  </BranchComponent>;
};